import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useClient } from 'urql';
import { Icon } from 'semantic-ui-react';
import CustomModal from '../../configuracion/components/customModal';
import PlanDeCuentaDropdown, { PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX } from '../../../containers/PlanDeCuentaDropdown/PlanDeCuentaDropdown';
import ObligationsDropdown from '../../../containers/ObligationsDropdown/ObligationsDropdown';
import ContraparteDropdown from '../../../containers/ContraparteDropdown/ContraparteDropdown';
import CreditLoanDropdown, { formatCreditLoanLabel } from './CreditLoanDropdown';
import BankPasswordDropdown from './BankPasswordDropdown';
import AddDashedOutlineButton from '../../general/js/forms/reconcile/contabilidad/AddDashedOutlineButton';
import { CreateAgenda, FIND_AGENDA } from '../../../graphql/agenda';
import { CreditsEvolution } from '../../../graphql/credits';
import { cleanRut, formatRut } from '../../../utils/formatter';
import './CreateRuleModal.scss';

const EXCEPTION_KIND_AGENDA = 'AGENDA_ID';
const EXCEPTION_KIND_BANK_CREDIT_PRINCIPAL = 'BANK_CREDIT_PRINCIPAL';
const EXCEPTION_KIND_BANK_PASSWORD_ID = 'BANK_PASSWORD_ID';

const newExceptionRow = (exceptionKind = EXCEPTION_KIND_AGENDA) => ({
  _localId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  exceptionKind,
  agendaId: '',
  creditId: '',
  passwordId: '',
  accountKey: '',
  accountName: '',
});

const mapExceptionFromRule = (ex) => {
  const passwordFilter = (ex.filters || []).find((f) => f.kind === EXCEPTION_KIND_BANK_PASSWORD_ID);
  if (passwordFilter) {
    return {
      _localId: `password-${passwordFilter.value || 'ex'}-${ex.accountKey}-${Math.random().toString(36).slice(2, 7)}`,
      exceptionKind: EXCEPTION_KIND_BANK_PASSWORD_ID,
      agendaId: '',
      creditId: '',
      passwordId: passwordFilter.value != null ? String(passwordFilter.value) : '',
      accountKey: ex.accountKey || '',
      accountName: ex.accountName || '',
    };
  }
  const bankFilter = (ex.filters || []).find((f) => f.kind === EXCEPTION_KIND_BANK_CREDIT_PRINCIPAL);
  if (bankFilter) {
    return {
      _localId: `credit-${bankFilter.value || 'ex'}-${ex.accountKey}-${Math.random().toString(36).slice(2, 7)}`,
      exceptionKind: EXCEPTION_KIND_BANK_CREDIT_PRINCIPAL,
      agendaId: '',
      creditId: bankFilter.value != null ? String(bankFilter.value) : '',
      passwordId: '',
      accountKey: ex.accountKey || '',
      accountName: ex.accountName || '',
    };
  }
  const agendaFromFilter = (ex.filters || []).find((f) => f.kind === EXCEPTION_KIND_AGENDA)?.agendaId;
  const agendaId =
    agendaFromFilter != null && agendaFromFilter !== '' ? String(agendaFromFilter) : '';
  return {
    _localId: `${agendaId || 'ex'}-${ex.accountKey}-${Math.random().toString(36).slice(2, 7)}`,
    exceptionKind: EXCEPTION_KIND_AGENDA,
    agendaId,
    creditId: '',
    passwordId: '',
    accountKey: ex.accountKey || '',
    accountName: ex.accountName || '',
  };
};

const CreateRuleModal = ({
  open,
  onClose,
  onSave,
  editingRule,
  isSaving = false,
  initialAccountKey = '',
  initialAccountName = '',
  initialFormValues = null,
}) => {
  const { t } = useTranslation('configuracion');
  const { selectedCompany } = useSelector((state) => state.companyList || {});
  const companyId = selectedCompany?.id;
  const urqlClient = useClient();
  const { executeMutation: createAgenda } = CreateAgenda();
  const { data: creditsData } = CreditsEvolution(companyId);

  const [formData, setFormData] = useState({
    description: '',
    transactionType: 'ALL',
    type: '',
    typeLabel: '',
    accountKey: '',
    accountName: '',
    exceptions: [],
  });

  const [errors, setErrors] = useState({});
  /** agendaId -> "RUT :: nombre" para mostrar excepciones al editar cuando el contacto no está en la primera página de búsqueda */
  const [agendaLabelById, setAgendaLabelById] = useState({});

  const exceptionAgendaIdsFingerprint = React.useMemo(() => {
    const list = editingRule?.exceptions;
    if (!list?.length) return '';
    return list
      .map((ex) => {
        const fromF = (ex.filters || []).find((f) => f.kind === 'AGENDA_ID')?.agendaId;
        return String(fromF || '');
      })
      .filter(Boolean)
      .sort()
      .join('|');
  }, [editingRule?.id, editingRule?.exceptions]);

  useEffect(() => {
    if (editingRule) {
      setFormData({
        description: editingRule.description || '',
        transactionType: editingRule.transactionType || 'ALL',
        type: editingRule.type || '',
        typeLabel: editingRule.typeLabel || '',
        accountKey: editingRule.accountKey || '',
        accountName: editingRule.accountName || '',
        exceptions: (editingRule.exceptions || []).map(mapExceptionFromRule),
      });
    } else {
      setFormData({
        description: initialFormValues?.description || '',
        transactionType: initialFormValues?.transactionType || 'ALL',
        type: initialFormValues?.type || '',
        typeLabel: initialFormValues?.typeLabel || '',
        accountKey: initialFormValues?.accountKey || initialAccountKey || '',
        accountName: initialFormValues?.accountName || initialAccountName || '',
        exceptions: [],
      });
    }
    setErrors({});
  }, [editingRule, open, initialAccountKey, initialAccountName, initialFormValues]);

  useEffect(() => {
    if (!open || !companyId) {
      setAgendaLabelById({});
      return;
    }
    if (!editingRule?.exceptions?.length) {
      setAgendaLabelById({});
      return;
    }
    const ids = [
      ...new Set(
        editingRule.exceptions
          .map((ex) => {
            const fromF = (ex.filters || []).find((f) => f.kind === 'AGENDA_ID')?.agendaId;
            return fromF != null && fromF !== '' ? String(fromF) : '';
          })
          .filter(Boolean),
      ),
    ];
    if (!ids.length) {
      setAgendaLabelById({});
      return;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      for (const id of ids) {
        try {
          const r = await urqlClient.query(FIND_AGENDA, { id, companyId }).toPromise();
          const a = r.data?.Agenda;
          if (a?.name != null) {
            const rutDisp = a.worldWide === 'cl' ? formatRut(a.rut) : a.rut;
            next[id] = `${rutDisp} :: ${a.name}`;
          }
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setAgendaLabelById(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, companyId, urqlClient, editingRule, exceptionAgendaIdsFingerprint]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleDocumentTypeChange = (e, { value, text, obligation }) => {
    setFormData((prev) => ({
      ...prev,
      type: value || '',
      typeLabel: text || obligation?.typeLabel || '',
    }));
    if (errors.type) {
      setErrors((prev) => ({ ...prev, type: null }));
    }
  };

  const handleAccountChange = (e, data) => {
    setFormData((prev) => ({
      ...prev,
      accountKey: data?.value || '',
      accountName: data?.label || '',
    }));
    if (errors.accountKey) {
      setErrors((prev) => ({ ...prev, accountKey: null }));
    }
  };

  const handleAddException = () => {
    setFormData((prev) => ({
      ...prev,
      exceptions: [...(prev.exceptions || []), newExceptionRow()],
    }));
    if (errors.exceptions) {
      setErrors((prev) => ({ ...prev, exceptions: null }));
    }
  };

  const handleRemoveException = (localId) => {
    setFormData((prev) => ({
      ...prev,
      exceptions: (prev.exceptions || []).filter((row) => row._localId !== localId),
    }));
  };

  const handleExceptionContraparteChange = async (localId, e, data) => {
    if (
      data?.isNew &&
      data?.label === 'RUT encontrado' &&
      data?.rut &&
      data?.name &&
      companyId
    ) {
      const digits = String(data.rut).replace(/[^0-9Kk]/g, '');
      const cleanedRut = cleanRut(digits);
      if (!cleanedRut || String(cleanedRut).replace(/[^0-9Kk]/g, '').length < 8) {
        setErrors((prev) => ({ ...prev, exceptions: t('createRuleModal.invalidRut') }));
        return;
      }
      const { data: resData, error } = await createAgenda({
        input: {
          companyId: String(companyId),
          name: String(data.name).trim(),
          rut: cleanedRut,
          isClient: true,
          isProvider: true,
          paymentDaysClient: 30,
          paymentDaysProvider: 30,
        },
      });
      if (error) {
        setErrors((prev) => ({
          ...prev,
          exceptions: t('createRuleModal.linkContactError'),
        }));
        return;
      }
      const ok = !!resData?.createAgenda?.status;
      const agendaId = resData?.createAgenda?.agenda?.id;
      if (ok && agendaId) {
        const idStr = String(agendaId);
        setFormData((prev) => ({
          ...prev,
          exceptions: (prev.exceptions || []).map((row) =>
            row._localId === localId ? { ...row, agendaId: idStr } : row,
          ),
        }));
        setAgendaLabelById((prev) => ({
          ...prev,
          [idStr]: data?.text && String(data.text).trim() ? String(data.text).trim() : prev[idStr],
        }));
        setErrors((prev) => ({ ...prev, exceptions: null }));
      } else {
        setErrors((prev) => ({
          ...prev,
          exceptions: resData?.createAgenda?.message || t('createRuleModal.linkContactNotFound'),
        }));
      }
      return;
    }

    const value = data?.value;
    if (value === 'add' || data?.isNew) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      exceptions: (prev.exceptions || []).map((row) =>
        row._localId === localId
          ? {
              ...row,
              agendaId: value ? String(value) : '',
            }
          : row,
      ),
    }));
    if (value && data?.text && String(data.text).trim()) {
      setAgendaLabelById((prev) => ({ ...prev, [String(value)]: String(data.text).trim() }));
    }
    if (errors.exceptions) {
      setErrors((prev) => ({ ...prev, exceptions: null }));
    }
  };

  const handleExceptionKindChange = (localId, kind) => {
    setFormData((prev) => ({
      ...prev,
      exceptions: (prev.exceptions || []).map((row) =>
        row._localId === localId
          ? {
              ...row,
              exceptionKind: kind,
              agendaId: '',
              creditId: '',
              passwordId: '',
            }
          : row,
      ),
    }));
    if (errors.exceptions) {
      setErrors((prev) => ({ ...prev, exceptions: null }));
    }
  };

  const handleExceptionCreditChange = (localId, e, data) => {
    const value = data?.value;
    setFormData((prev) => ({
      ...prev,
      exceptions: (prev.exceptions || []).map((row) =>
        row._localId === localId
          ? {
              ...row,
              creditId: value ? String(value) : '',
            }
          : row,
      ),
    }));
    if (errors.exceptions) {
      setErrors((prev) => ({ ...prev, exceptions: null }));
    }
  };

  const handleExceptionPasswordChange = (localId, e, data) => {
    const value = data?.value;
    setFormData((prev) => ({
      ...prev,
      exceptions: (prev.exceptions || []).map((row) =>
        row._localId === localId
          ? {
              ...row,
              passwordId: value ? String(value) : '',
            }
          : row,
      ),
    }));
    if (errors.exceptions) {
      setErrors((prev) => ({ ...prev, exceptions: null }));
    }
  };

  const getCreditLabelById = (creditId) => {
    if (!creditId) return '';
    const list = creditsData?.Credits?.credits || [];
    const found = list.find((c) => String(c._id) === String(creditId));
    return found ? formatCreditLoanLabel(found) : '';
  };

  const handleExceptionAccountChange = (localId, e, data) => {
    setFormData((prev) => ({
      ...prev,
      exceptions: (prev.exceptions || []).map((row) =>
        row._localId === localId
          ? {
              ...row,
              accountKey: data?.value || '',
              accountName: data?.label || '',
            }
          : row,
      ),
    }));
    if (errors.exceptions) {
      setErrors((prev) => ({ ...prev, exceptions: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.transactionType) {
      newErrors.transactionType = t('createRuleModal.movementTypeRequired');
    }

    if (!formData.type) {
      newErrors.type = t('createRuleModal.documentTypeRequired');
    }

    if (!formData.accountKey) {
      newErrors.accountKey = t('createRuleModal.accountRequired');
    }

    for (const ex of formData.exceptions || []) {
      const partial = !!(
        ex.agendaId ||
        ex.creditId ||
        ex.passwordId ||
        ex.accountKey ||
        ex.accountName
      );
      const completeAgenda =
        ex.exceptionKind === EXCEPTION_KIND_AGENDA &&
        ex.agendaId &&
        ex.accountKey &&
        ex.accountName;
      const completeCredit =
        ex.exceptionKind === EXCEPTION_KIND_BANK_CREDIT_PRINCIPAL &&
        ex.creditId &&
        ex.accountKey &&
        ex.accountName;
      const completePassword =
        ex.exceptionKind === EXCEPTION_KIND_BANK_PASSWORD_ID &&
        ex.passwordId &&
        ex.accountKey &&
        ex.accountName;
      if (partial && !completeAgenda && !completeCredit && !completePassword) {
        newErrors.exceptions =
          ex.exceptionKind === EXCEPTION_KIND_BANK_CREDIT_PRINCIPAL
            ? t('createRuleModal.exceptionCreditIncomplete')
            : ex.exceptionKind === EXCEPTION_KIND_BANK_PASSWORD_ID
              ? t('createRuleModal.exceptionPasswordIncomplete')
            : t('createRuleModal.exceptionAgendaIncomplete');
        break;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(formData);
    }
  };

  const handleClose = () => {
    setFormData({
      description: '',
      transactionType: 'ALL',
      type: '',
      typeLabel: '',
      accountKey: '',
      accountName: '',
      exceptions: [],
    });
    setErrors({});
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      title={editingRule ? t('createRuleModal.editTitle') : t('createRuleModal.createTitle')}
      content={
        <div className="create-rule-modal-content">
          <div className="create-rule-field create-rule-type-document-row">
            <div className="create-rule-type-col">
              <label className="create-rule-label" htmlFor="rule-transaction-type">
                {t('createRuleModal.movementTypeLabel')} <span className="create-rule-required">*</span>
              </label>
              <select
                id="rule-transaction-type"
                className={`create-rule-select ${errors.transactionType ? 'error' : ''}`}
                value={formData.transactionType}
                onChange={(e) => handleFieldChange('transactionType', e.target.value)}
              >
                <option value="ALL">{t('createRuleModal.movementAll')}</option>
                <option value="RECIEVED">{t('createRuleModal.movementReceived')}</option>
                <option value="SPEND">{t('createRuleModal.movementSpend')}</option>
              </select>
              {errors.transactionType && (
                <div className="create-rule-error">{errors.transactionType}</div>
              )}
            </div>
            <div className="create-rule-document-col">
              <label className="create-rule-label" htmlFor="rule-document-type">
                {t('createRuleModal.documentTypeLabel')} <span className="create-rule-required">*</span>
              </label>
              <ObligationsDropdown
                id="rule-document-type"
                value={formData.type}
                onChange={handleDocumentTypeChange}
                placeholder={t('createRuleModal.documentTypePlaceholder')}
                className={errors.type ? 'error' : ''}
                includeDocumentosTributarios
                includeRendiciones
              />
              {errors.type && <div className="create-rule-error">{errors.type}</div>}
            </div>
          </div>

          <div className="create-rule-field">
            <label className="create-rule-label">
              {t('createRuleModal.accountLabel')} <span className="create-rule-required">*</span>
            </label>
            <PlanDeCuentaDropdown
              value={formData.accountKey}
              onChange={handleAccountChange}
              placeholder={t('createRuleModal.accountPlaceholder')}
              className={errors.accountKey ? 'error' : ''}
              portalMenu
              menuZIndex={PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.MODAL}
            />
            {errors.accountKey && <div className="create-rule-error">{errors.accountKey}</div>}
            <div className="create-rule-hint">
              {t('createRuleModal.accountHint')}
            </div>
          </div>

          <div className="create-rule-field">
            <label className="create-rule-label" htmlFor="rule-description">
              {t('createRuleModal.descriptionLabel')}{' '}
              <span className="create-rule-optional">{t('createRuleModal.optional')}</span>
            </label>
            <input
              id="rule-description"
              type="text"
              className={`create-rule-input ${errors.description ? 'error' : ''}`}
              value={formData.description}
              placeholder={t('createRuleModal.descriptionPlaceholder')}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </div>

          <div className="create-rule-field create-rule-exceptions-section">
            <label className="create-rule-label">{t('createRuleModal.exceptionsLabel')}</label>
            <div className="create-rule-hint create-rule-hint--block">
              {t('createRuleModal.exceptionsHint')}
            </div>
            {(formData.exceptions || []).map((row) => (
              <div key={row._localId} className="create-rule-exception-card">
                <div className="create-rule-exception-kind">
                  <label className="create-rule-sublabel" htmlFor={`ex-kind-${row._localId}`}>
                    {t('createRuleModal.exceptionKindLabel')}
                  </label>
                  <select
                    id={`ex-kind-${row._localId}`}
                    className="create-rule-select"
                    value={row.exceptionKind || EXCEPTION_KIND_AGENDA}
                    onChange={(e) => handleExceptionKindChange(row._localId, e.target.value)}
                  >
                    <option value={EXCEPTION_KIND_AGENDA}>{t('createRuleModal.exceptionKindAgenda')}</option>
                    <option value={EXCEPTION_KIND_BANK_CREDIT_PRINCIPAL}>
                      {t('createRuleModal.exceptionKindCredit')}
                    </option>
                    <option value={EXCEPTION_KIND_BANK_PASSWORD_ID}>
                      {t('createRuleModal.exceptionKindPassword')}
                    </option>
                  </select>
                </div>
                {row.exceptionKind === EXCEPTION_KIND_BANK_PASSWORD_ID ? (
                  <div className="create-rule-exception-rut">
                    <label className="create-rule-sublabel" htmlFor={`ex-password-${row._localId}`}>
                      {t('createRuleModal.bankPasswordLabel')}
                    </label>
                    {companyId ? (
                      <BankPasswordDropdown
                        id={`ex-password-${row._localId}`}
                        value={row.passwordId || ''}
                        onChange={(e, data) => handleExceptionPasswordChange(row._localId, e, data)}
                        companyId={companyId}
                      />
                    ) : (
                      <div className="create-rule-error">{t('createRuleModal.selectCompany')}</div>
                    )}
                  </div>
                ) : row.exceptionKind === EXCEPTION_KIND_BANK_CREDIT_PRINCIPAL ? (
                  <div className="create-rule-exception-rut">
                    <label className="create-rule-sublabel" htmlFor={`ex-credit-${row._localId}`}>
                      {t('createRuleModal.bankLoanLabel')}
                    </label>
                    {companyId ? (
                      <CreditLoanDropdown
                        id={`ex-credit-${row._localId}`}
                        value={row.creditId || ''}
                        onChange={(e, data) => handleExceptionCreditChange(row._localId, e, data)}
                        companyId={companyId}
                        selectedTextOverride={getCreditLabelById(row.creditId) || undefined}
                      />
                    ) : (
                      <div className="create-rule-error">{t('createRuleModal.selectCompany')}</div>
                    )}
                  </div>
                ) : (
                  <div className="create-rule-exception-rut">
                    <label className="create-rule-sublabel" htmlFor={`ex-contraparte-${row._localId}`}>
                      {t('createRuleModal.counterpartyLabel')}
                    </label>
                    {companyId ? (
                      <ContraparteDropdown
                        id={`ex-contraparte-${row._localId}`}
                        value={row.agendaId || ''}
                        onChange={(e, data) => handleExceptionContraparteChange(row._localId, e, data)}
                        placeholder={t('createRuleModal.searchContactPlaceholder')}
                        companyId={companyId}
                        allowCreate={false}
                        allowRutLookup
                        portalMenu
                        selectedTextOverride={
                          row.agendaId ? agendaLabelById[String(row.agendaId)] || undefined : undefined
                        }
                      />
                    ) : (
                      <div className="create-rule-error">{t('createRuleModal.selectCompany')}</div>
                    )}
                  </div>
                )}
                <div className="create-rule-exception-row-2">
                  <div className="create-rule-exception-account">
                    <label className="create-rule-sublabel" htmlFor={`ex-account-${row._localId}`}>
                      {t('createRuleModal.accountIfApplies')}
                    </label>
                    <PlanDeCuentaDropdown
                      id={`ex-account-${row._localId}`}
                      value={row.accountKey}
                      onChange={(e, data) => handleExceptionAccountChange(row._localId, e, data)}
                      placeholder={t('createRuleModal.accountShortPlaceholder')}
                      portalMenu
                      menuZIndex={PLAN_CUENTA_DROPDOWN_MENU_Z_INDEX.MODAL}
                    />
                  </div>
                  <div className="create-rule-exception-remove-wrap">
                    <span className="create-rule-sublabel create-rule-sublabel--spacer" aria-hidden="true">
                      &nbsp;
                    </span>
                    <button
                      type="button"
                      className="create-rule-exception-remove"
                      onClick={() => handleRemoveException(row._localId)}
                      title={t('createRuleModal.removeException')}
                      aria-label={t('createRuleModal.removeException')}
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {errors.exceptions && <div className="create-rule-error">{errors.exceptions}</div>}
            <AddDashedOutlineButton onClick={handleAddException} className="create-rule-add-exception">
              {t('createRuleModal.addException')}
            </AddDashedOutlineButton>
          </div>
        </div>
      }
      confirmLabel={editingRule ? t('common.saveChanges') : t('createRuleModal.confirmCreate')}
      onConfirm={handleSave}
      confirmDisabled={isSaving}
      isLoading={isSaving}
      cancelarLabel={t('common.cancel')}
      size="tiny"
      classNameModal="create-rule-modal"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        width: 'min(920px, 95vw)',
        maxWidth: '95vw',
        minWidth: 'min(920px, 95vw)',
        minHeight: '80vh',
        maxHeight: '80vh',
        overflow: 'hidden',
        margin: '0 auto',
      }}
    />
  );
};

export default CreateRuleModal;
